import { useLingui } from "@lingui/react";
import { formatNumber } from "@mobility-item-page/utilities";
import { useAppConfig } from "../../../../context/AppConfigProviderContext.tsx";
import FieldValuePair from "../../FieldValuePair.tsx";
import type { SpecProps } from "../types.ts";

type FuelStandard =
  | {
      text: string;
      tooltip: string;
    }
  | undefined;

export const FuelConsumption = ({ fuelConsumption }: SpecProps<"fuel_consumption">) => {
  const { i18n } = useLingui();
  const { locale } = useAppConfig();

  if (
    !fuelConsumption ||
    (fuelConsumption.combined_kilowatt_hours == null && fuelConsumption.combined_liters == null)
  ) {
    return null;
  }

  const getStandard = (value?: string): FuelStandard => {
    if (value === "WLTP") {
      return {
        text: "WLTP",
        tooltip: i18n.t({
          id: "itemPageLayout.keyInfo.tooltip.wltp",
          message:
            "WLTP is a metric from when the car was new and the actual range must be seen in context of age, km, driving pattern and weather conditions",
          comment: "Tooltip for Fuel Consumption (WLTP) in ad specification",
        }),
      };
    }
    if (value === "NEDC") {
      return {
        text: "NEDC",
        tooltip: i18n.t({
          id: "itemPageLayout.keyInfo.tooltip.nedc",
          message:
            "NEDC was the pan-European measurement method used to determine the range and fuel consumption of cars before it was replaced by WLTP",
          comment: "Tooltip for Fuel Consumption (NEDC) in ad specification",
        }),
      };
    }
    return undefined;
  };

  const standard = getStandard(fuelConsumption.standard?.value);
  const fieldWithStandard = (baseField: string) => (standard?.text ? `${baseField} (${standard.text})` : baseField);

  if (fuelConsumption.combined_kilowatt_hours != null) {
    const field = i18n.t({
      id: "itemPageLayout.keyInfo.energy_consumption",
      message: "Energy consumption",
      comment: "Field for Energy consumption in ad specification",
    });

    const value = i18n.t({
      id: "itemPageLayout.keyInfo.energy_consumption.postfix",
      message: "{consumption} kWh/100 km",
      comment: "Fuel consumption (electric) postfix in ad specification",
      values: { consumption: formatNumber(fuelConsumption.combined_kilowatt_hours, locale) },
    });

    return <FieldValuePair field={fieldWithStandard(field)} value={value} tooltip={standard?.tooltip} />;
  }

  if (fuelConsumption.combined_liters != null && fuelConsumption.combined_liters_unit != null) {
    const field = i18n.t({
      id: "itemPageLayout.keyInfo.fuel_consumption",
      message: "Fuel consumption",
      comment: "Field for 'fuel consumption' in ad specification",
    });

    const value =
      fuelConsumption.combined_liters_unit === "LITRES_PER_100_KILOMETERS"
        ? i18n.t({
            id: "itemPageLayout.keyInfo.fuel_consumption.postfix.liters_per_100_km",
            message: "{consumption} L/100 km",
            comment: "Fuel consumption in liters pr 100 kilometers postfix in ad specification",
            values: { consumption: formatNumber(fuelConsumption.combined_liters, locale) },
          })
        : i18n.t({
            id: "itemPageLayout.keyInfo.fuel_consumption.postfix.kilometers_per_liter",
            message: "{consumption} km/L",
            comment: "Fuel consumption in km per liter postfix in ad specification",
            values: { consumption: formatNumber(fuelConsumption.combined_liters, locale) },
          });

    return <FieldValuePair field={fieldWithStandard(field)} value={value} tooltip={standard?.tooltip} />;
  }

  return null;
};
